import React, { useEffect, useState } from 'react';
import GuestLayout from '../../../layouts/GuestLayout';
import { Shield, Lock, Eye, Database, Users, Globe } from 'lucide-react';

const PrivacyPolicy = () => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // small timeout ensures initial classes are applied before animating
    const t = setTimeout(() => setMounted(true), 20);
    return () => clearTimeout(t);
  }, []);

  return (
    <GuestLayout>
      <div className={`min-h-screen bg-gradient-to-b from-gray-50 to-white py-12 transition-all duration-500 ease-in transform ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-50 rounded-full mb-6">
              <Shield className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Privacy Policy
            </h1>
            {/* <p className="text-lg text-gray-600">
              Effective Date: {new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p> */}
            <div className="mt-4 inline-flex items-center px-4 py-2 bg-red-50 rounded-full">
              <Lock className="w-4 h-4 text-red-600 mr-2" />
              <span className="text-sm font-medium text-red-600">
                Protecting your data is our priority
              </span>
            </div>
          </div>

          {/* Main Content */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            {/* Introduction */}
            <section className="mb-10">
              <div className="flex items-start mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-red-600 font-bold text-xl">1</span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Introduction</h2>
                  <p className="text-gray-700">
                    At TXT Connect ("we," "our," or "us"), we respect your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our bulk messaging platform and services.
                  </p>
                </div>
              </div>
              <div className="ml-16 bg-red-50 p-6 rounded-lg">
                <p className="text-gray-700 italic">
                  "Personal Data" refers to any information that can be used to identify you, either directly or indirectly, in accordance with the Ghana Data Protection Regulation (NDPR) and other applicable data protection laws.
                </p>
              </div>
            </section>

            {/* Data Collection */}
            <section className="mb-10">
              <div className="flex items-start mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <Database className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Information We Collect</h2>
                  <p className="text-gray-700 mb-4">
                    We collect several types of information to provide and improve our services:
                  </p>
                </div>
              </div>
              
              <div className="ml-16 space-y-6">
                <div className="p-5 border border-gray-200 rounded-xl">
                  <h3 className="font-bold text-red-600 mb-3 text-lg">Personal Information</h3>
                  <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                    <li><strong>Account Information:</strong> Name, email address, phone number, company name</li>
                    <li><strong>Billing Information:</strong> Payment details, billing address, transaction history</li>
                    <li><strong>Contact Information:</strong> Business address, phone numbers, email addresses</li>
                  </ul>
                </div>

                <div className="p-5 border border-gray-200 rounded-xl">
                  <h3 className="font-bold text-red-600 mb-3 text-lg">Usage Data</h3>
                  <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                    <li><strong>Service Usage:</strong> SMS sent/received, API calls, login times, feature usage</li>
                    <li><strong>Technical Data:</strong> IP addresses, browser type, device information, operating system</li>
                    <li><strong>Performance Data:</strong> Delivery rates, message status, response times</li>
                  </ul>
                </div>

                <div className="p-5 border border-gray-200 rounded-xl">
                  <h3 className="font-bold text-red-600 mb-3 text-lg">Message Content</h3>
                  <p className="text-gray-700 mb-3">
                    We process SMS content on your behalf to deliver messages. Message content is:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                    <li>End-to-end encrypted during transmission</li>
                    <li>Stored temporarily for delivery purposes only</li>
                    <li>Not used for advertising or profiling purposes</li>
                    <li>Deleted from our servers after successful delivery</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Data Usage */}
            <section className="mb-10">
              <div className="flex items-start mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <Eye className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">How We Use Your Information</h2>
                  <p className="text-gray-700 mb-4">
                    We use collected information for the following purposes:
                  </p>
                </div>
              </div>

              <div className="ml-16 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-5 bg-red-50 rounded-xl">
                  <h3 className="font-bold text-gray-900 mb-3">Service Delivery</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Process and deliver SMS messages</li>
                    <li>• Maintain and improve our platform</li>
                    <li>• Provide customer support</li>
                    <li>• Process payments and subscriptions</li>
                  </ul>
                </div>

                <div className="p-5 bg-red-50 rounded-xl">
                  <h3 className="font-bold text-gray-900 mb-3">Communication</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Send service updates and notifications</li>
                    <li>• Respond to inquiries and support requests</li>
                    <li>• Send marketing communications (with consent)</li>
                    <li>• Provide account notifications</li>
                  </ul>
                </div>

                <div className="p-5 bg-red-50 rounded-xl">
                  <h3 className="font-bold text-gray-900 mb-3">Security & Compliance</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Detect and prevent fraud</li>
                    <li>• Ensure legal compliance</li>
                    <li>• Maintain service security</li>
                    <li>• Enforce our terms and policies</li>
                  </ul>
                </div>

                <div className="p-5 bg-red-50 rounded-xl">
                  <h3 className="font-bold text-gray-900 mb-3">Improvement</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Analyze usage patterns</li>
                    <li>• Develop new features</li>
                    <li>• Improve user experience</li>
                    <li>• Conduct research and analysis</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Data Sharing */}
            <section className="mb-10">
              <div className="flex items-start mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <Users className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Sharing & Disclosure</h2>
                  <p className="text-gray-700">
                    We do not sell your personal data. We may share information in the following limited circumstances:
                  </p>
                </div>
              </div>

              <div className="ml-16 space-y-4">
                <div className="p-4 border border-red-200 rounded-lg">
                  <h3 className="font-semibold text-red-600 mb-2">Service Providers</h3>
                  <p className="text-gray-700">
                    We engage trusted third-party service providers to assist with operations, such as payment processing, SMS routing, cloud hosting, and customer support. These providers are contractually bound to protect your data.
                  </p>
                </div>

                <div className="p-4 border border-red-200 rounded-lg">
                  <h3 className="font-semibold text-red-600 mb-2">Legal Requirements</h3>
                  <p className="text-gray-700">
                    We may disclose information if required by law, regulation, legal process, or governmental request, or to protect the rights, property, or safety of TXT Connect, our users, or the public.
                  </p>
                </div>

                <div className="p-4 border border-red-200 rounded-lg">
                  <h3 className="font-semibold text-red-600 mb-2">Business Transfers</h3>
                  <p className="text-gray-700">
                    In connection with a merger, acquisition, or sale of assets, your information may be transferred. We will notify you of any such change and outline your choices.
                  </p>
                </div>
              </div>
            </section>

            {/* Data Security */}
            <section className="mb-10">
              <div className="flex items-start mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <Lock className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Security</h2>
                  <p className="text-gray-700 mb-4">
                    We implement robust security measures to protect your data:
                  </p>
                </div>
              </div>

              <div className="ml-16">
                <div className="bg-gradient-to-r from-red-50 to-pink-50 p-6 rounded-xl mb-6">
                  <h3 className="font-bold text-gray-900 mb-4">Our Security Measures Include:</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <p className="text-gray-700 flex items-center">
                        <span className="w-2 h-2 bg-red-600 rounded-full mr-3"></span>
                        End-to-end encryption for all messages
                      </p>
                      <p className="text-gray-700 flex items-center">
                        <span className="w-2 h-2 bg-red-600 rounded-full mr-3"></span>
                        Secure socket layer (SSL) technology
                      </p>
                      <p className="text-gray-700 flex items-center">
                        <span className="w-2 h-2 bg-red-600 rounded-full mr-3"></span>
                        Regular security audits and penetration testing
                      </p>
                    </div>
                    <div className="space-y-2">
                      <p className="text-gray-700 flex items-center">
                        <span className="w-2 h-2 bg-red-600 rounded-full mr-3"></span>
                        Access controls and authentication protocols
                      </p>
                      <p className="text-gray-700 flex items-center">
                        <span className="w-2 h-2 bg-red-600 rounded-full mr-3"></span>
                        Data encryption at rest and in transit
                      </p>
                      <p className="text-gray-700 flex items-center">
                        <span className="w-2 h-2 bg-red-600 rounded-full mr-3"></span>
                        Regular security training for staff
                      </p>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700">
                  While we implement industry-standard security measures, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security but we strive to use commercially acceptable means to protect your personal data.
                </p>
              </div>
            </section>

            {/* International Transfers */}
            <section className="mb-10">
              <div className="flex items-start mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <Globe className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">International Data Transfers</h2>
                  <p className="text-gray-700">
                    As a Ghanaian based company, we primarily store and process data within Ghana. However, to provide global SMS delivery, we may transfer message data to telecommunications partners in other countries. All such transfers comply with applicable data protection laws.
                  </p>
                </div>
              </div>
            </section>

            {/* Your Rights */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Data Protection Rights</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-5 bg-gray-50 rounded-xl">
                  <h3 className="font-bold text-gray-900 mb-3">Right to Access</h3>
                  <p className="text-gray-700">
                    You have the right to request copies of your personal data that we hold.
                  </p>
                </div>
                <div className="p-5 bg-gray-50 rounded-xl">
                  <h3 className="font-bold text-gray-900 mb-3">Right to Rectification</h3>
                  <p className="text-gray-700">
                    You may request correction of inaccurate or incomplete personal data.
                  </p>
                </div>
                <div className="p-5 bg-gray-50 rounded-xl">
                  <h3 className="font-bold text-gray-900 mb-3">Right to Erasure</h3>
                  <p className="text-gray-700">
                    You may request deletion of your personal data under certain conditions.
                  </p>
                </div>
                <div className="p-5 bg-gray-50 rounded-xl">
                  <h3 className="font-bold text-gray-900 mb-3">Right to Restrict Processing</h3>
                  <p className="text-gray-700">
                    You may request restriction of processing your personal data.
                  </p>
                </div>
                <div className="p-5 bg-gray-50 rounded-xl">
                  <h3 className="font-bold text-gray-900 mb-3">Right to Object</h3>
                  <p className="text-gray-700">
                    You may object to our processing of your personal data.
                  </p>
                </div>
                <div className="p-5 bg-gray-50 rounded-xl">
                  <h3 className="font-bold text-gray-900 mb-3">Right to Data Portability</h3>
                  <p className="text-gray-700">
                    You may request transfer of your data to another organization.
                  </p>
                </div>
              </div>
              {/* <div className="mt-6 p-4 bg-red-50 rounded-lg">
                <p className="text-gray-700">
                  To exercise any of these rights, please contact us at <span className="font-semibold">privacy@txtconnect.com</span>. We will respond within 30 days as required by NDPR.
                </p>
              </div> */}
            </section>

            {/* Contact Section */}
            {/* <section className="mt-12 pt-8 border-t border-gray-200">
              <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-xl p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Data Protection Officer</h4>
                    <p className="text-gray-700">Email: dpo@txtconnect.com</p>
                    <p className="text-gray-700">Phone: +234 800 TXT CONNECT</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">General Privacy Inquiries</h4>
                    <p className="text-gray-700">Email: privacy@txtconnect.com</p>
                    <p className="text-gray-700">Address: 123 Tech Street, Lagos, Nigeria</p>
                  </div>
                </div>
              </div>
            </section> */}
          </div>

          {/* Footer Note */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              We may update this Privacy Policy periodically. We will notify you of any material changes by posting the new policy on this page and updating the effective date.
            </p>
            <p className="mt-2 text-sm text-gray-500">
              This policy complies with the Ghana Data Protection Regulation  and other applicable data protection laws.
            </p>
          </div>
        </div>
      </div>
    </GuestLayout>
  );
};

export default PrivacyPolicy;